import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudIcon from '@mui/icons-material/Cloud';
import ShieldIcon from '@mui/icons-material/Shield';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import BoltIcon from '@mui/icons-material/Bolt';
import PolicyIcon from '@mui/icons-material/Policy';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import VerifiedIcon from '@mui/icons-material/Verified';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';

const PRIMARY = '#FF7518';
const DARK = '#4a2d08';
const DARK_BG = '#0f0f0f';
const SECTION_BG = '#fafafa';

const features = [
  {
    icon: <ShieldIcon sx={{ fontSize: 36, color: PRIMARY }} />,
    title: '245+ Healthcare Rules',
    description: 'Comprehensive rule library covering HIPAA, FHIR, HITRUST, SOC 2, GDPR, and 3 cloud providers.',
  },
  {
    icon: <BoltIcon sx={{ fontSize: 36, color: PRIMARY }} />,
    title: 'Lightning Fast Scans',
    description: 'Complete compliance scans in under 5 seconds. Integrate into your CI/CD pipeline without slowing down deployments.',
  },
  {
    icon: <CloudIcon sx={{ fontSize: 36, color: PRIMARY }} />,
    title: 'Multi-Cloud Support',
    description: 'Native support for AWS HealthLake, Azure Health Data Services, and GCP Healthcare API.',
  },
  {
    icon: <IntegrationInstructionsIcon sx={{ fontSize: 36, color: PRIMARY }} />,
    title: 'CI/CD Integrations',
    description: 'GitHub Actions, Jenkins, and Terraform plugins to automate compliance checks in your pipeline.',
  },
  {
    icon: <PolicyIcon sx={{ fontSize: 36, color: PRIMARY }} />,
    title: 'Audit-Ready Exports',
    description: 'Generate annotated PDFs, executive summaries, and evidence packages mapped to HIPAA 164.308/164.312.',
  },
  {
    icon: <VerifiedIcon sx={{ fontSize: 36, color: PRIMARY }} />,
    title: 'FHIR Compliance',
    description: 'Deep FHIR R4/R5 validation for HL7 standards, resource conformance, and secure data exchange.',
  },
];

const steps = [
  {
    number: '01',
    title: 'Get Your Token',
    description: 'Sign up and generate your CLI token from the dashboard in seconds.',
    code: '# Get your token from https://baastream.com\nexport BAASTREAM_TOKEN=your_token_here',
  },
  {
    number: '02',
    title: 'Download the CLI',
    description: 'Install the BaaStream CLI with a single command. Available for macOS, Linux, and Windows.',
    code: 'curl -sSL https://raw.githubusercontent.com/amulyakashyap09/baastream-public/master/install.sh | sh',
  },
  {
    number: '03',
    title: 'Run Your First Scan',
    description: 'Point the scanner at your config files, FHIR resources, or log files and get instant results.',
    code: './baastream scan fhir_server_config.json',
  },
];

const complianceFrameworks = [
  { name: 'HIPAA', description: 'Health Insurance Portability and Accountability Act', rules: '85+', color: '#2563eb' },
  { name: 'FHIR', description: 'Fast Healthcare Interoperability Resources', rules: '40+', color: '#16a34a' },
  { name: 'HITRUST', description: 'Health Information Trust Alliance CSF', rules: '35+', color: '#9333ea' },
  { name: 'SOC 2', description: 'Service Organization Control 2', rules: '30+', color: '#dc2626' },
  { name: 'GDPR', description: 'General Data Protection Regulation', rules: '25+', color: '#ea580c' },
  { name: 'ISO 27001', description: 'Information Security Management', rules: '20+', color: '#0891b2' },
];

const pricingTiers = [
  {
    id: 'free',
    name: 'Community',
    price: 0,
    description: 'For individual developers and personal projects',
    popular: false,
    features: [
      '50 scans / month',
      '10 basic security rules',
      'Text summary output',
      'Community support (Discord)',
      '15-day trial of full features',
    ],
    cta: 'Start Free',
    ctaVariant: 'outlined' as const,
  },
  {
    id: 'starter',
    name: 'Starter',
    price: 99,
    description: 'Perfect for healthcare startups and seed-stage companies',
    popular: false,
    features: [
      '300 scans / month',
      '25 core security rules',
      'PDF report exports',
      'HIPAA basics coverage',
      'GDPR basics coverage',
      'Community + email support',
    ],
    cta: 'Start Free Scan',
    ctaVariant: 'outlined' as const,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 249,
    description: 'Perfect for growing and established healthcare teams',
    popular: true,
    features: [
      '1,200 scans / month',
      '75 advanced security rules',
      'JSON, CSV, PDF, executive summary',
      'Complete HIPAA + GDPR + HITRUST',
      'Multi-cloud support',
      '5 custom rules / month',
      'Advanced analytics & dashboards',
      '24h priority support',
    ],
    cta: 'Start Free Scan',
    ctaVariant: 'contained' as const,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 1299,
    description: 'For large healthcare organizations and enterprises',
    popular: false,
    features: [
      '10,000 scans / month',
      '99+ rules — all frameworks',
      'Audit-ready export packages',
      'White-label reports',
      'Team management & RBAC',
      '50 custom rules / month',
      'All CI/CD integrations',
      'Dedicated support engineer',
      'Quarterly compliance training',
    ],
    cta: 'Contact Sales',
    ctaVariant: 'outlined' as const,
  },
];

const faqs = [
  {
    question: 'What is BaaStream?',
    answer:
      'BaaStream is a CLI-based compliance scanner purpose-built for healthcare DevOps. It scans your configuration files, FHIR resources, logs, and infrastructure code against 245+ rules spanning HIPAA, FHIR, HITRUST, SOC 2, GDPR, and major cloud providers.',
  },
  {
    question: 'How quickly can I get started?',
    answer:
      'In under 5 minutes. Sign up, grab your token, install the CLI, and run your first scan. No agents, no SaaS collectors, no firewall changes — the scanner runs entirely in your environment.',
  },
  {
    question: 'What cloud platforms are supported?',
    answer:
      'BaaStream has native rules for AWS HealthLake, Azure Health Data Services (Azure HDS), and Google Cloud Healthcare API. It also supports on-premises and hybrid deployments.',
  },
  {
    question: 'Does BaaStream integrate with CI/CD pipelines?',
    answer:
      'Yes. Pro and Enterprise plans include plugins for GitHub Actions, Jenkins, and Terraform. This lets you gate deployments on compliance pass/fail automatically.',
  },
  {
    question: 'What is a "scan"?',
    answer:
      'One scan = running baastream-scan against one target (a file, directory, or log file). Running many rules in a single invocation still counts as one scan.',
  },
  {
    question: 'Can I use BaaStream for HIPAA audits?',
    answer:
      'BaaStream maps findings directly to HIPAA Security Rule implementation specifications (§164.308 and §164.312). Enterprise plans include audit-ready evidence packages in annotated PDF and Excel formats.',
  },
  {
    question: 'Is there a free plan?',
    answer:
      'Yes. The Community plan is free forever with 50 scans/month and 10 basic rules. Every new account also gets a 15-day full-feature trial (extended to 30 days for healthcare organizations).',
  },
  {
    question: 'How is data handled? Does BaaStream see my PHI?',
    answer:
      'No. The CLI runs entirely within your infrastructure. Scan results are transmitted to the dashboard over TLS, but BaaStream never has access to the underlying PHI in your files.',
  },
];

export function HomePage() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [billingAnnual, setBillingAnnual] = useState(false);
  const [promoDismissed, setPromoDismissed] = useState(false);

  const navLinks = [
    { label: 'Features', href: '#features' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'Docs', href: '/docs' },
  ];

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  return (
    <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh' }}>
      {/* ── Promo Banner ── */}
      {!promoDismissed && (
        <Box
          sx={{
            bgcolor: PRIMARY,
            py: 1,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            position: 'relative',
          }}
        >
          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600, textAlign: 'center' }}>
            🎉 First month free on any paid plan — no credit card required.{' '}
            <Box
              component="span"
              onClick={() => scrollTo('pricing')}
              sx={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 700 }}
            >
              See plans
            </Box>
          </Typography>
          <Box
            component="span"
            onClick={() => setPromoDismissed(true)}
            sx={{ position: 'absolute', right: 12, cursor: 'pointer', color: 'rgba(255,255,255,0.8)', fontSize: '1.1rem', lineHeight: 1, '&:hover': { color: '#fff' } }}
          >
            ×
          </Box>
        </Box>
      )}
      {/* ── Navbar ── */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          color: DARK,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ py: 0.5 }}>
            {/* Logo */}
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', flexGrow: 1 }}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <HealthAndSafetyIcon sx={{ color: PRIMARY, fontSize: 30 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: DARK, letterSpacing: -0.5 }}>
                BaaStream
              </Typography>
            </Box>

            {/* Desktop Nav */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
              {navLinks.map((link) => (
                <Button
                  key={link.label}
                  onClick={() => link.href.startsWith('#') ? scrollTo(link.href.replace('#', '')) : navigate(link.href)}
                  sx={{ color: '#555', fontWeight: 500, '&:hover': { color: PRIMARY } }}
                >
                  {link.label}
                </Button>
              ))}
              {/* <Button
                startIcon={<KeyIcon />}
                onClick={() => navigate('/token')}
                sx={{ color: '#555', fontWeight: 500 }}
              >
                Get Token
              </Button>
              <Button
                startIcon={<DownloadIcon />}
                sx={{ color: '#555', fontWeight: 500 }}
              >
                Download CLI
              </Button> */}
              <Button
                variant="contained"
                onClick={() => navigate('/register')}
                sx={{
                  bgcolor: PRIMARY,
                  ml: 1,
                  px: 3,
                  '&:hover': { bgcolor: '#e05e00' },
                }}
              >
                Start Free Scan
              </Button>
            </Box>

            {/* Mobile Menu Button */}
            <IconButton
              sx={{ display: { md: 'none' } }}
              onClick={() => setMobileOpen(true)}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer anchor="right" open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <Box sx={{ width: 260, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <IconButton onClick={() => setMobileOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          {navLinks.map((link) => (
            <Button
              key={link.label}
              fullWidth
              onClick={() => link.href.startsWith('#') ? scrollTo(link.href.replace('#', '')) : navigate(link.href)}
              sx={{ justifyContent: 'flex-start', color: DARK, mb: 0.5 }}
            >
              {link.label}
            </Button>
          ))}
          <Divider sx={{ my: 1 }} />
          <Button fullWidth onClick={() => navigate('/register')} variant="contained" sx={{ bgcolor: PRIMARY }}>
            Start Free Scan
          </Button>
          <Button fullWidth onClick={() => navigate('/login')} sx={{ mt: 1, color: DARK }}>
            Sign In
          </Button>
        </Box>
      </Drawer>

      {/* ── Hero ── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${DARK_BG} 0%, #1a0a00 50%, #2d1200 100%)`,
          color: '#fff',
          pt: { xs: 10, md: 14 },
          pb: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-30%',
            right: '-10%',
            width: '60%',
            height: '160%',
            background: `radial-gradient(ellipse, rgba(255,117,24,0.12) 0%, transparent 70%)`,
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Chip
                label="Healthcare DevOps Compliance"
                size="small"
                sx={{
                  bgcolor: 'rgba(255,117,24,0.15)',
                  color: PRIMARY,
                  border: `1px solid rgba(255,117,24,0.3)`,
                  mb: 3,
                  fontWeight: 600,
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.4rem', md: '3.2rem' },
                  fontWeight: 800,
                  color: '#fff',
                  lineHeight: 1.15,
                  mb: 2,
                  letterSpacing: -1,
                }}
              >
                Close The Gap in{' '}
                <Box component="span" sx={{ color: PRIMARY }}>
                  Healthcare DevOps
                </Box>
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '1.15rem', lineHeight: 1.7, mb: 4, maxWidth: 520 }}
              >
                BaaStream scans your infrastructure and FHIR configs against 245+ rules across HIPAA, FHIR,
                HITRUST, SOC 2, GDPR, and 3 cloud providers — in under 5 seconds.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/register')}
                  sx={{
                    bgcolor: PRIMARY,
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    '&:hover': { bgcolor: '#e05e00' },
                  }}
                >
                  Start Free Scan
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.3)',
                    color: '#fff',
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    '&:hover': { borderColor: PRIMARY, color: PRIMARY },
                  }}
                >
                  Book Demo
                </Button>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 4 }}>
                {[
                  { label: 'No credit card required' },
                  { label: 'Cancel anytime' },
                  { label: 'Unlimited scans on paid plans' },
                ].map((item) => (
                  <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CheckIcon sx={{ fontSize: 14, color: '#4ade80' }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.78rem' }}>
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Terminal Demo */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  bgcolor: '#111',
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
                }}
              >
                {/* Terminal header */}
                <Box sx={{ bgcolor: '#1e1e1e', px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff5f57' }} />
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#febc2e' }} />
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#28c840' }} />
                  <Typography sx={{ ml: 1, color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                    bash — baastream
                  </Typography>
                </Box>
                {/* Terminal body */}
                <Box sx={{ p: 3, fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 1.8 }}>
                  <Box sx={{ color: 'rgba(255,255,255,0.4)' }}>$ ./baastream scan fhir_server_config.json</Box>
                  <Box sx={{ color: 'rgba(255,255,255,0.7)', mt: 1 }}>
                    🔍 Scanning <Box component="span" sx={{ color: '#60a5fa' }}>fhir_server_config.json</Box>
                  </Box>
                  <Box sx={{ color: 'rgba(255,255,255,0.5)' }}>   Loading 75 Pro rules...</Box>
                  <Box sx={{ color: 'rgba(255,255,255,0.5)', mt: 1 }}>   ✓ HIPAA §164.312(a)(2)(iv) — Encryption</Box>
                  <Box sx={{ color: '#fbbf24' }}>   ⚠ FHIR R4 — Patient.meta.security missing</Box>
                  <Box sx={{ color: 'rgba(255,255,255,0.5)' }}>   ✓ GDPR Art. 25 — Data minimization</Box>
                  <Box sx={{ color: 'rgba(255,255,255,0.5)' }}>   ✓ SOC 2 CC6.1 — Access control</Box>
                  <Box sx={{ color: 'rgba(255,255,255,0.5)' }}>   ✓ HealthLake — KMS encryption enabled</Box>
                  <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.1)' }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleOutlineIcon sx={{ color: '#4ade80', fontSize: 18 }} />
                    <Box component="span" sx={{ color: '#4ade80', fontWeight: 700 }}>
                      Scan completed in 2.4s
                    </Box>
                    <Box component="span" sx={{ color: 'rgba(255,255,255,0.4)', ml: 2 }}>
                      74 passed · 1 warning · 0 critical
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Stats Banner ── */}
      <Box sx={{ bgcolor: PRIMARY, py: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={2} justifyContent="center">
            {[
              { value: '245+', label: 'Healthcare Rules' },
              { value: '6', label: 'Compliance Frameworks' },
              { value: '3', label: 'Cloud Providers' },
              { value: '< 5s', label: 'Average Scan Time' },
            ].map((stat) => (
              <Grid size={{ xs: 6, sm: 3 }} key={stat.label} sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#fff', lineHeight: 1 }}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.5 }}>
                  {stat.label}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── The Gap in Healthcare DevOps ── */}
      <Box sx={{ bgcolor: SECTION_BG, py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" sx={{ fontWeight: 800, color: DARK, mb: 2, fontSize: { xs: '2rem', md: '2.6rem' } }}>
              The Gap in Healthcare DevOps
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', maxWidth: 640, mx: 'auto', fontSize: '1.1rem', lineHeight: 1.7 }}>
              Healthcare engineering teams ship faster than ever — but compliance tooling hasn't kept up.
              BaaStream bridges that gap with developer-native compliance scanning.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card sx={{ height: '100%', border: '1px solid #fee2e2', bgcolor: '#fff' }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <WarningAmberIcon sx={{ color: '#ef4444', fontSize: 28 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#ef4444' }}>
                      Without BaaStream
                    </Typography>
                  </Box>
                  {[
                    'Compliance reviews happen at the end of sprints — too late',
                    'Manual checklists miss edge cases in FHIR and cloud configs',
                    'Audit prep takes weeks of manual evidence collection',
                    'HIPAA violations discovered in production, not in dev',
                    'No visibility into compliance posture across teams',
                  ].map((item) => (
                    <Box key={item} sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                      <CloseIcon sx={{ color: '#ef4444', fontSize: 18, mt: 0.3, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.6 }}>
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card sx={{ height: '100%', border: `1px solid rgba(255,117,24,0.3)`, bgcolor: '#fff' }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <CheckCircleOutlineIcon sx={{ color: PRIMARY, fontSize: 28 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY }}>
                      With BaaStream
                    </Typography>
                  </Box>
                  {[
                    'Compliance checks run on every commit, automatically',
                    '245+ curated rules catch FHIR, HIPAA, and cloud issues',
                    'Audit-ready evidence packages generated in one command',
                    'Violations caught in seconds before they reach staging',
                    'Team-wide compliance dashboard with trend analytics',
                  ].map((item) => (
                    <Box key={item} sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                      <CheckIcon sx={{ color: PRIMARY, fontSize: 18, mt: 0.3, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.6 }}>
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Features ── */}
      <Box id="features" sx={{ bgcolor: '#fff', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip label="Features" size="small" sx={{ bgcolor: 'rgba(255,117,24,0.1)', color: PRIMARY, fontWeight: 600, mb: 2 }} />
            <Typography variant="h2" sx={{ fontWeight: 800, color: DARK, mb: 2, fontSize: { xs: '2rem', md: '2.6rem' } }}>
              Everything you need for healthcare compliance
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', maxWidth: 540, mx: 'auto', fontSize: '1.05rem' }}>
              Built for DevOps and DevSecOps teams that need compliance to move at the speed of engineering.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {features.map((feature) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={feature.title}>
                <Card
                  sx={{
                    height: '100%',
                    p: 0,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(255,117,24,0.12)' },
                  }}
                >
                  <CardContent sx={{ p: 3.5 }}>
                    <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: DARK, mb: 1 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.7 }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── How It Works ── */}
      <Box sx={{ bgcolor: SECTION_BG, py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip label="Quick Start" size="small" sx={{ bgcolor: 'rgba(255,117,24,0.1)', color: PRIMARY, fontWeight: 600, mb: 2 }} />
            <Typography variant="h2" sx={{ fontWeight: 800, color: DARK, mb: 2, fontSize: { xs: '2rem', md: '2.6rem' } }}>
              Start scanning in 5 minutes
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', maxWidth: 480, mx: 'auto' }}>
              No agents, no SaaS collectors, no firewall changes. The scanner runs entirely in your environment.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {steps.map((step, i) => (
              <Grid size={{ xs: 12, md: 4 }} key={step.number}>
                <Box sx={{ position: 'relative' }}>
                  {i < steps.length - 1 && (
                    <Box
                      sx={{
                        display: { xs: 'none', md: 'block' },
                        position: 'absolute',
                        top: 28,
                        right: -24,
                        width: 48,
                        height: 2,
                        bgcolor: 'rgba(255,117,24,0.3)',
                        zIndex: 1,
                      }}
                    />
                  )}
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: '50%',
                        bgcolor: PRIMARY,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1rem' }}>{step.number}</Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: DARK }}>
                      {step.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#666', mb: 2.5, lineHeight: 1.7 }}>
                    {step.description}
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: '#1a1a1a',
                      borderRadius: 1.5,
                      p: 2,
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      color: '#a3e635',
                      whiteSpace: 'pre',
                      overflowX: 'auto',
                    }}
                  >
                    {step.code}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<DownloadIcon />}
              onClick={() => navigate('/register')}
              sx={{ bgcolor: PRIMARY, px: 5, py: 1.5, '&:hover': { bgcolor: '#e05e00' } }}
            >
              Download CLI &amp; Start Free
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ── Full Compliance Coverage ── */}
      <Box sx={{ bgcolor: '#fff', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip label="Compliance Rules" size="small" sx={{ bgcolor: 'rgba(255,117,24,0.1)', color: PRIMARY, fontWeight: 600, mb: 2 }} />
            <Typography variant="h2" sx={{ fontWeight: 800, color: DARK, mb: 2, fontSize: { xs: '2rem', md: '2.6rem' } }}>
              Full Compliance Coverage
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', maxWidth: 560, mx: 'auto', fontSize: '1.05rem' }}>
              245+ rules across HIPAA, FHIR, HITRUST, SOC 2, GDPR, and 3 cloud providers — all maintained and updated as regulations change.
            </Typography>
          </Box>

          <Grid container spacing={3} justifyContent="center">
            {complianceFrameworks.map((fw) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={fw.name}>
                <Card
                  sx={{
                    textAlign: 'center',
                    p: 0,
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'translateY(-4px)' },
                  }}
                >
                  <CardContent sx={{ p: 3.5 }}>
                    <Chip
                      label={fw.name}
                      sx={{
                        bgcolor: `${fw.color}15`,
                        color: fw.color,
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        mb: 1.5,
                        border: `1px solid ${fw.color}30`,
                      }}
                    />
                    <Typography variant="body2" sx={{ color: '#666', mb: 1.5, lineHeight: 1.5 }}>
                      {fw.description}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: DARK }}>
                      {fw.rules}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999' }}>
                      rules covered
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* HIPAA Security Rule Mapping */}
          <Box
            sx={{
              mt: 8,
              p: { xs: 3, md: 5 },
              background: `linear-gradient(135deg, ${DARK} 0%, #2d1a00 100%)`,
              borderRadius: 3,
              color: '#fff',
            }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid size={{ xs: 12, md: 7 }}>
                <Chip
                  label="HIPAA Security Rule Mapping"
                  size="small"
                  sx={{ bgcolor: 'rgba(255,117,24,0.2)', color: PRIMARY, fontWeight: 600, mb: 2 }}
                />
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#fff', mb: 2 }}>
                  Every finding maps to a HIPAA specification
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.8, mb: 3 }}>
                  BaaStream maps each rule violation directly to HIPAA Security Rule implementation specifications —
                  §164.308 (Administrative Safeguards) and §164.312 (Technical Safeguards). Your audit team gets
                  evidence that maps 1-to-1 with what auditors actually look for.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/register')}
                  sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#e05e00' } }}
                >
                  View HIPAA Rule Mapping
                </Button>
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <Box
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.05)',
                    borderRadius: 2,
                    p: 3,
                    border: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  {[
                    { spec: '§164.308(a)(1)', label: 'Risk Analysis', status: 'covered' },
                    { spec: '§164.308(a)(5)', label: 'Security Awareness', status: 'covered' },
                    { spec: '§164.312(a)(1)', label: 'Access Control', status: 'covered' },
                    { spec: '§164.312(a)(2)(iv)', label: 'Encryption & Decryption', status: 'covered' },
                    { spec: '§164.312(e)(2)(ii)', label: 'Transmission Security', status: 'covered' },
                  ].map((item) => (
                    <Box
                      key={item.spec}
                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}
                    >
                      <Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'monospace' }}>
                          {item.spec}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>
                          {item.label}
                        </Typography>
                      </Box>
                      <Chip
                        label="Covered"
                        size="small"
                        sx={{ bgcolor: 'rgba(74,222,128,0.15)', color: '#4ade80', border: '1px solid rgba(74,222,128,0.3)' }}
                      />
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* ── Pricing ── */}
      <Box id="pricing" sx={{ bgcolor: SECTION_BG, py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip label="Pricing" size="small" sx={{ bgcolor: 'rgba(255,117,24,0.1)', color: PRIMARY, fontWeight: 600, mb: 2 }} />
            <Typography variant="h2" sx={{ fontWeight: 800, color: DARK, mb: 2, fontSize: { xs: '2rem', md: '2.6rem' } }}>
              Simple, transparent pricing
            </Typography>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,117,24,0.08)', border: '1px solid rgba(255,117,24,0.25)', borderRadius: 2, px: 2, py: 0.75, mb: 3 }}>
              <Typography variant="body2" sx={{ color: PRIMARY, fontWeight: 700 }}>
                🎉 First month free on any paid plan
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ color: '#666', mb: 4 }}>
              All plans include unlimited users. No credit card required for Community.
            </Typography>
            {/* Billing toggle */}
            <Box
              sx={{
                display: 'inline-flex',
                bgcolor: '#e5e7eb',
                borderRadius: 3,
                p: 0.5,
              }}
            >
              <Button
                size="small"
                onClick={() => setBillingAnnual(false)}
                sx={{
                  borderRadius: 2.5,
                  px: 3,
                  bgcolor: !billingAnnual ? '#fff' : 'transparent',
                  color: !billingAnnual ? DARK : '#888',
                  boxShadow: !billingAnnual ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: !billingAnnual ? '#fff' : 'rgba(0,0,0,0.04)' },
                }}
              >
                Monthly
              </Button>
              <Button
                size="small"
                onClick={() => setBillingAnnual(true)}
                sx={{
                  borderRadius: 2.5,
                  px: 3,
                  bgcolor: billingAnnual ? '#fff' : 'transparent',
                  color: billingAnnual ? DARK : '#888',
                  boxShadow: billingAnnual ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: billingAnnual ? '#fff' : 'rgba(0,0,0,0.04)' },
                }}
              >
                Annual{' '}
                <Chip
                  label="Save 16%"
                  size="small"
                  sx={{ ml: 0.5, bgcolor: 'rgba(255,117,24,0.15)', color: PRIMARY, height: 18, fontSize: '0.65rem', fontWeight: 700 }}
                />
              </Button>
            </Box>
          </Box>

          <Grid container spacing={3} alignItems="stretch">
            {pricingTiers.map((tier) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={tier.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: tier.popular ? `2px solid ${PRIMARY}` : '1px solid rgba(0,0,0,0.08)',
                    position: 'relative',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 16px 40px rgba(0,0,0,0.12)' },
                  }}
                >
                  {tier.popular && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -14,
                        left: '50%',
                        transform: 'translateX(-50%)',
                      }}
                    >
                      <Chip
                        label="Most Popular"
                        size="small"
                        sx={{ bgcolor: PRIMARY, color: '#fff', fontWeight: 700, px: 1 }}
                      />
                    </Box>
                  )}
                  <CardContent sx={{ p: 3.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: DARK, mb: 0.5 }}>
                      {tier.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#888', mb: 2.5, display: 'block', minHeight: 32 }}>
                      {tier.description}
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      {tier.price === 0 ? (
                        <Typography variant="h3" sx={{ fontWeight: 800, color: DARK }}>
                          Free
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                          <Typography variant="h3" sx={{ fontWeight: 800, color: DARK }}>
                            ${billingAnnual ? Math.round(tier.price * 0.833) : tier.price}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#888' }}>/mo</Typography>
                        </Box>
                      )}
                      {billingAnnual && tier.price > 0 && (
                        <Typography variant="caption" sx={{ color: '#888' }}>
                          Billed annually
                        </Typography>
                      )}
                    </Box>
                    <List dense disablePadding sx={{ flexGrow: 1 }}>
                      {tier.features.map((f) => (
                        <ListItem key={f} disableGutters sx={{ py: 0.4 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <CheckIcon sx={{ fontSize: 15, color: tier.popular ? PRIMARY : '#4ade80' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={f}
                            primaryTypographyProps={{ variant: 'body2', color: '#555', lineHeight: 1.4 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                    <Button
                      fullWidth
                      variant={tier.ctaVariant}
                      onClick={() => navigate(tier.id === 'enterprise' ? '/login' : '/register')}
                      sx={{
                        mt: 3,
                        ...(tier.popular
                          ? { bgcolor: PRIMARY, color: '#fff', '&:hover': { bgcolor: '#e05e00' } }
                          : { borderColor: tier.ctaVariant === 'outlined' ? PRIMARY : undefined, color: tier.ctaVariant === 'outlined' ? PRIMARY : undefined }),
                      }}
                    >
                      {tier.cta}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body2" sx={{ color: '#888' }}>
              Cancel anytime · All plans include unlimited scans on paid tiers · Need a custom quote?{' '}
              <Box component="a" href="mailto:sales@baastream.com" sx={{ color: PRIMARY, textDecoration: 'none', fontWeight: 600 }}>
                Contact sales
              </Box>
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── FAQ ── */}
      <Box sx={{ bgcolor: '#fff', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <Chip label="FAQ" size="small" sx={{ bgcolor: 'rgba(255,117,24,0.1)', color: PRIMARY, fontWeight: 600, mb: 2 }} />
            <Typography variant="h2" sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '2rem', md: '2.4rem' } }}>
              Everything you need to know about BaaStream
            </Typography>
          </Box>

          {faqs.map((faq, i) => (
            <Accordion
              key={i}
              disableGutters
              elevation={0}
              sx={{
                mb: 1,
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: '10px !important',
                '&:before': { display: 'none' },
                '&.Mui-expanded': { border: `1px solid rgba(255,117,24,0.3)` },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: PRIMARY }} />}
                sx={{ px: 3, py: 0.5 }}
              >
                <Typography variant="body1" sx={{ fontWeight: 600, color: DARK }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.8 }}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* ── Bottom CTA ── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${PRIMARY} 0%, #e05e00 100%)`,
          py: { xs: 8, md: 10 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 800, color: '#fff', mb: 2, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
            Ready to close the compliance gap?
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)', mb: 4, fontSize: '1.1rem' }}>
            Join healthcare engineering teams scanning with BaaStream. Free forever — no credit card required.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                bgcolor: '#fff',
                color: PRIMARY,
                px: 5,
                py: 1.5,
                fontWeight: 700,
                fontSize: '1rem',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
              }}
            >
              Start Free Scan
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/login')}
              sx={{
                borderColor: 'rgba(255,255,255,0.5)',
                color: '#fff',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              Sign In
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ── Footer ── */}
      <Box sx={{ bgcolor: DARK_BG, color: 'rgba(255,255,255,0.6)', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} sx={{ mb: 5 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <HealthAndSafetyIcon sx={{ color: PRIMARY, fontSize: 26 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
                  BaaStream
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ lineHeight: 1.8, maxWidth: 280 }}>
                Healthcare DevOps compliance scanning. 245+ rules. Runs in your environment. Audit-ready exports.
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#fff', mb: 2 }}>
                Product
              </Typography>
              {[
                { label: 'Features', href: '/features' },
                { label: 'Pricing', href: '/plans' },
                { label: 'Changelog', href: '/changelog' },
                { label: 'Roadmap', href: '/roadmap' },
              ].map((l) => (
                <Typography
                  key={l.label}
                  component="a"
                  href={l.href}
                  variant="body2"
                  sx={{ display: 'block', mb: 1, color: 'rgba(255,255,255,0.7)', textDecoration: 'none', '&:hover': { color: PRIMARY } }}
                >
                  {l.label}
                </Typography>
              ))}
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#fff', mb: 2 }}>
                Docs
              </Typography>
              {[
                { label: 'Quick Start', href: '/docs#quick-start' },
                { label: 'CLI Reference', href: '/docs#cli-reference' },
                { label: 'Rule Library', href: '/docs#rule-library' },
                { label: 'Integrations', href: '/docs#integrations' },
              ].map((l) => (
                <Typography
                  key={l.label}
                  component="a"
                  href={l.href}
                  variant="body2"
                  sx={{ display: 'block', mb: 1, color: 'rgba(255,255,255,0.7)', textDecoration: 'none', '&:hover': { color: PRIMARY } }}
                >
                  {l.label}
                </Typography>
              ))}
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#fff', mb: 2 }}>
                Company
              </Typography>
              {[
                { label: 'About Us', href: '/about' },
                { label: 'Blog', href: '#' },
                { label: 'Careers', href: '/careers' },
                { label: 'Contact', href: '/contact' },
              ].map((l) => (
                <Typography
                  key={l.label}
                  component="a"
                  href={l.href}
                  variant="body2"
                  sx={{ display: 'block', mb: 1, color: 'rgba(255,255,255,0.7)', textDecoration: 'none', '&:hover': { color: PRIMARY } }}
                >
                  {l.label}
                </Typography>
              ))}
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#fff', mb: 2 }}>
                Support
              </Typography>
              {[
                { label: 'Contact Us', href: '/contact' },
                { label: 'Pricing', href: '/plans' },
                { label: 'Privacy Policy', href: '/privacy-policy' },
                { label: 'Refund Policy', href: '/refund-policy' },
                { label: 'Terms & Conditions', href: '/terms' },
              ].map((link) => (
                <Typography
                  key={link.label}
                  component="a"
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{
                    display: 'block',
                    mb: 1,
                    color: 'rgba(255,255,255,0.6)',
                    textDecoration: 'none',
                    '&:hover': { color: PRIMARY },
                  }}
                >
                  {link.label}
                </Typography>
              ))}
            </Grid>
          </Grid>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 3 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
              © 2025 Monks Do Work. All rights reserved. BaaStream is a product of Monks Do Work.
            </Typography>
            <Box sx={{ display: 'flex', gap: 3 }}>
              {[
                { label: 'Privacy Policy', href: '/privacy-policy' },
                { label: 'Terms of Service', href: '/terms' },
                { label: 'Refund Policy', href: '/refund-policy' },
              ].map((link) => (
                <Typography
                  key={link.label}
                  component="a"
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.3)', textDecoration: 'none', '&:hover': { color: PRIMARY } }}
                >
                  {link.label}
                </Typography>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
